import React, { useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { goToPosition, useGamePlay } from "../../redux/gamePlay";
import MoveButtons from "./MoveButtons";
import GameResult from "./GameResult";
import { buildGameplayMoveRecords } from "./gamePlayMoves";

function MoveCell({
  move,
  position,
  selected,
}: {
  move: ReturnType<typeof buildGameplayMoveRecords>[number] | null;
  position: number;
  selected: boolean;
}) {
  if (!move) {
    return <div className="flex-1 min-w-0" />;
  }

  return (
    <button
      type="button"
      onClick={() => goToPosition(position)}
      className={cn(
        "flex flex-1 items-center min-w-0 h-full px-2 text-left font-semibold hover:bg-gray-500/15",
        selected ? "text-primary  " : "text-gray-500",
      )}
      aria-label={move.san}
    >
      <span className="block truncate">{move.san}</span>
    </button>
  );
}

export default function Moves() {
  const {
    moves,
    positions,
    positionIndex,
    gameEnded,
    result,
    status,
    playingWhite,
    opponent,
  } = useGamePlay();

  const gameResultProps = {
    result,
    status,
    playingWhite,
    opponentName: opponent.displayName,
  };

  const movesDiv = useRef<HTMLDivElement>(null);
  const moveRecords = useMemo(
    () => buildGameplayMoveRecords(moves, positions?.[0]),
    [moves, positions],
  );

  useEffect(() => {
    const container = movesDiv.current;
    if (!container) return;

    if (positionIndex === moves.length || moves.length === 0) {
      container.scrollTop = container.scrollHeight;
    } else {
      container.scrollTop = Math.floor(positionIndex / 2 - 0.1) * 36;
    }
  }, [positionIndex, moves.length]);

  useEffect(() => {
    const container = movesDiv.current;
    if (gameEnded && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [gameEnded]);

  function renderMoves() {
    const rows = [];
    let index = 1;

    for (let i = 0; i < moveRecords.length; i += 2) {
      const whiteMove = moveRecords[i];
      const blackMove = moveRecords[i + 1] ?? null;
      const rowClass = index % 2 === 0 ? "bg-gray-200/50" : "bg-gray-50";

      rows.push(
        <div
          key={`${i}-${whiteMove?.from}${whiteMove?.to}-${
            blackMove?.from ?? ""
          }${blackMove?.to ?? ""}`}
          className={cn(
            "box-border flex h-[36px] min-h-[36px] w-full shrink-0 items-center text-sm leading-[36px]",
            rowClass,
          )}
        >
          <div className="pl-3.5 w-10 text-gray-500 font-semibold shrink-0">
            {index}
          </div>
          <MoveCell
            move={whiteMove}
            position={i + 1}
            selected={positionIndex === i + 1}
          />
          <MoveCell
            move={blackMove}
            position={i + 2}
            selected={positionIndex === i + 2}
          />
        </div>,
      );
      index++;
    }

    return rows;
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-gray-50">
      <div
        ref={movesDiv}
        className="flex flex-col flex-1 w-full overflow-auto text-sm text-gray-500 select-none"
        style={{ scrollbarGutter: "stable" }}
      >
        {renderMoves()}
        {gameEnded && (
          <div className="flex-col items-center hidden pt-5 md:flex">
            <GameResult {...gameResultProps} />
          </div>
        )}
      </div>
      <MoveButtons />
    </div>
  );
}
